import type { RoleId } from "../api/client";

/**
 * The Whānau Factor Profile role-permissions matrix.
 *
 * Read-only canonical view of what each role can do across the app.
 * Renders on /admin/permissions for Sue, Gerhard, and any administrator
 * who wants a single-page reference of the access posture.
 *
 * This file is the SOURCE OF TRUTH for the matrix. Code enforcement
 * (server-side role checks, client-side conditional UI) should follow
 * what's stated here. When you change a permission, update the matrix
 * here AND the corresponding gate in the code that enforces it.
 */

/**
 * Binary access: allowed or denied. The four-state model (yes / own /
 * with-override / no) was simplified for admin UX — Sue and Gerhard
 * wanted a single switch per cell rather than a four-way dropdown.
 *
 * Where the running app still needs the nuance (e.g. 'own caseload'
 * scoping or 'with override' audit events), those are now expressed
 * in code at the enforcement site, not encoded in the matrix itself.
 */
export type AccessState = boolean;

export type PermissionRow = {
  /** Stable identifier; mirrors how the permission is gated in code. */
  id: string;
  /** Group heading for visual organisation. */
  group: "whanau" | "sessions" | "team" | "admin" | "system";
  /** What the permission lets you do. Used as the matrix row label. */
  label: string;
  /** Slightly longer prose for the description column. */
  description: string;
  /** Per-role access state. Order doesn't matter, all five must be present. */
  access: Record<RoleId, AccessState>;
};

export const PERMISSION_GROUPS: Array<{
  id: PermissionRow["group"];
  label: string;
}> = [
  { id: "whanau", label: "Whānau profile" },
  { id: "sessions", label: "Assessment sessions" },
  { id: "team", label: "Team and caseload" },
  { id: "admin", label: "Admin functions" },
  { id: "system", label: "Platform" },
];

export const PERMISSIONS_MATRIX: PermissionRow[] = [
  // ----- Whānau profile ---------------------------------------------------
  {
    id: "whanau.view.own",
    group: "whanau",
    label: "View whānau in own caseload",
    description:
      "See profiles for whānau where this user is recorded as a keyworker.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "whanau.view.any",
    group: "whanau",
    label: "View any whānau (cross-team)",
    description:
      "View profiles outside own caseload. Override is audit-logged as whanau.viewed.override.",
    access: {
      kaimahi: false,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "whanau.edit",
    group: "whanau",
    label: "Edit whānau profile fields",
    description:
      "Change display name, primary location, iwi, engagement state.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "whanau.share-mode",
    group: "whanau",
    label: "Enter whānau share mode",
    description:
      "Hand the tablet to whānau for a guided view. Requires PIN setup.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },

  // ----- Sessions ---------------------------------------------------------
  {
    id: "sessions.record",
    group: "sessions",
    label: "Record an assessment session",
    description:
      "Run the wizard, set subdomain states and notes, save a session that updates the current factor profile.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "sessions.email-supervisor",
    group: "sessions",
    label: "CC supervisor on session email",
    description:
      "Add an optional supervisor email to the session notification at save time.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "sessions.correct",
    group: "sessions",
    label: "Correct a saved assessment",
    description:
      "Edit an existing session entry. Audit-logged as assessment.corrected.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },

  // ----- Team and caseload ------------------------------------------------
  {
    id: "team.reassign",
    group: "team",
    label: "Reassign whānau between kaimahi",
    description:
      "Change which kaimahi is the keyworker for a whānau. Phase 2 functionality.",
    access: {
      kaimahi: false,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "team.signoff",
    group: "team",
    label: "Approve / return assessment sessions",
    description:
      "Sign off on team members' sessions or return them for revision. Phase 2.",
    access: {
      kaimahi: false,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "team.dashboards",
    group: "team",
    label: "View team dashboards",
    description:
      "Aggregate views across team or service caseload. Phase 3 reporting.",
    access: {
      kaimahi: false,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },

  // ----- Admin functions --------------------------------------------------
  {
    id: "admin.view-audit",
    group: "admin",
    label: "View audit log",
    description:
      "Read the audit_event timeline of every recorded action on the platform.",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "admin.manage-roles",
    group: "admin",
    label: "Assign and revoke roles",
    description:
      "Promote kaimahi to higher roles, demote, or revoke. Self-revoke is blocked.",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: false,
      "super-admin": true,
    },
  },
  {
    id: "admin.reset-pin",
    group: "admin",
    label: "Reset another user's share PIN",
    description:
      "Clear someone else's share-mode PIN so they can set a new one.",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: false,
      "super-admin": true,
    },
  },
  {
    id: "admin.user-lookup",
    group: "admin",
    label: "Look up Entra users via Graph",
    description:
      "Search the directory by name or email and resolve OIDs. Used by role management.",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: false,
      "super-admin": true,
    },
  },
  {
    id: "admin.appearance",
    group: "admin",
    label: "Edit tile photos and headings",
    description:
      "Change the home-screen tile imagery and welcome copy seen by every kaimahi. Phase 3.",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "admin.framework-content",
    group: "admin",
    label: "Edit framework content",
    description:
      "Adjust subdomain names, alert definitions, referral suggestions. Phase 3.",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: true,
      "super-admin": true,
    },
  },

  // ----- Platform / system ------------------------------------------------
  {
    id: "system.export-csv",
    group: "system",
    label: "Export CSV",
    description:
      "Export whānau data as CSV via Microsoft Graph. Audit-logged as export.csv.",
    access: {
      kaimahi: false,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "system.export-pdf",
    group: "system",
    label: "Export designed PDF",
    description:
      "Generate the formatted whānau report PDF. Audit-logged as export.pdf.",
    access: {
      kaimahi: true,
      manager: true,
      "exec-manager": true,
      administrator: true,
      "super-admin": true,
    },
  },
  {
    id: "reports.view",
    group: "system",
    label: "View the reporting dashboard",
    description:
      "Access /reports — the Te Pou Mataaho reporting dashboard. Aggregated, de-identified figures only. Enforced server-side via this matrix (super-admin by default).",
    access: {
      kaimahi: false,
      manager: false,
      "exec-manager": false,
      administrator: false,
      "super-admin": true,
    },
  },
];

export const ROLE_ORDER: RoleId[] = [
  "kaimahi",
  "manager",
  "exec-manager",
  "administrator",
  "super-admin",
];
